import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, File, Image, Mic, Paperclip, Send } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import PlayerNavigation from "../components/PlayerNavigation";
import PlayerNotificationBell from "../components/PlayerNotificationBell";
import PlayerRequestComposer from "../components/PlayerRequestComposer";
import PlayerRequestMessage from "../components/PlayerRequestMessage";
import PlayerRequestViewportDebug from "../components/PlayerRequestViewportDebug";
import PlayerVoiceRequest from "../components/PlayerVoiceRequest";
import api, { apiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtDateTime } from "../lib/time";
import { lockPlayerRequestDocument, watchPlayerRequestViewport } from "../lib/playerRequestViewport";
import { useAuth } from "../context/AuthContext";
import { playerCommunicationTime } from "../lib/playerNotificationDisplay";
import { playerRequestStatusKey, playerRequestTitle } from "../lib/playerRequestDisplay";
import "../player-communications.css";

const when = (value, timezone, lang) => fmtDateTime(value, timezone, lang);

function Status({ request }) {
  const { t } = useI18n();
  const value = playerRequestStatusKey(request);
  const color = value === "response_requested" ? "var(--pc-score-medium)"
    : value === "new_reply" ? "var(--pc-cyan)"
      : value === "completed" ? "var(--pc-score-good)" : "var(--pc-muted)";
  return <span className="pc-request-status" data-state={value} style={{ color }}>{t(`requestPlayer_${value}`)}</span>;
}

export default function PlayerRequests() {
  const { t, lang } = useI18n(); const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState("loading"); const [requests, setRequests] = useState([]);
  const [section, setSection] = useState("open"); const [message, setMessage] = useState("");
  const [reply, setReply] = useState(""); const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const openedRef = useRef(new Set()); const deepLinkRef = useRef(null);
  const shellRef = useRef(null); const messageAreaRef = useRef(null); const messageScrollRef = useRef({ id: null, count: 0 });
  const followLatestRef = useRef(true);
  const selectedId = searchParams.get("request");
  const selected = requests.find((row) => row.id === selectedId) || null;
  const selectedRequestId = selected?.id;
  const selectedTopic = selected ? playerRequestTitle(selected, t, { truncate: false }) : "";
  const viewportDebugEnabled = process.env.NODE_ENV !== "production" && searchParams.get("viewportDebug") === "1";

  useEffect(() => {
    if (!selected) return;
    const count = selected.messages?.length || 0;
    const previous = messageScrollRef.current;
    const shouldScroll = previous.id !== selected.id || count > previous.count;
    messageScrollRef.current = { id: selected.id, count };
    if (!shouldScroll) return;
    followLatestRef.current = true;
    const frame = requestAnimationFrame(() => {
      const area = messageAreaRef.current;
      if (area) area.scrollTop = area.scrollHeight;
    });
    return () => cancelAnimationFrame(frame);
  }, [selected]);

  useEffect(() => {
    if (!selectedRequestId) return undefined;
    const shell = shellRef.current;
    const unlockDocument = lockPlayerRequestDocument({
      documentElement: document.documentElement,
      body: document.body,
      root: document.getElementById("root"),
    });
    const stopWatching = watchPlayerRequestViewport({
      visualViewport: window.visualViewport,
      fallbackTarget: window,
      getLayoutHeight: () => window.innerHeight,
      onChange: ({ keyboardInset, keyboardOpen }) => {
        if (shell) {
          shell.style.setProperty("--pc-keyboard-inset", `${keyboardInset}px`);
          shell.dataset.keyboardOpen = keyboardOpen ? "true" : "false";
        }
      },
    });
    return () => {
      stopWatching();
      unlockDocument();
      if (shell) {
        shell.style.removeProperty("--pc-keyboard-inset");
        delete shell.dataset.keyboardOpen;
      }
    };
  }, [selectedRequestId]);

  const load = useCallback(async () => {
    setState("loading"); setError("");
    try { const { data } = await api.get("/me/requests"); setRequests(data.requests || []); setState("ready"); }
    catch (err) { setError(apiError(err.response?.data?.detail)); setState("error"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const replaceRequest = (updated) => setRequests((current) => {
    const exists = current.some((row) => row.id === updated.id);
    return (exists ? current.map((row) => row.id === updated.id ? updated : row) : [updated, ...current])
      .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
  });
  const openRequest = useCallback(async (id) => {
    openedRef.current.add(id);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("request", id);
      return next;
    }); setError("");
    try { const { data } = await api.post(`/me/requests/${id}/read`); replaceRequest(data.request); }
    catch (err) { setError(apiError(err.response?.data?.detail)); }
  }, [setSearchParams]);
  useEffect(() => {
    const target = requests.find((row) => row.id === selectedId);
    if (target && deepLinkRef.current !== selectedId) {
      deepLinkRef.current = selectedId;
      setSection(target.status === "resolved" ? "resolved" : "open");
      openRequest(selectedId);
    }
  }, [selectedId, requests, openRequest]);

  const activeRequests = useMemo(() => requests.filter((row) => row.status !== "resolved"), [requests]);
  const resolvedRequests = useMemo(() => requests.filter((row) => row.status === "resolved"), [requests]);
  const createText = async () => {
    if (!message.trim()) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/me/requests", { message: message.trim() });
      replaceRequest(data.request); setMessage(""); setSection("open"); setSearchParams({ request: data.request.id });
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/me/requests/${selected.id}/responses`, { message: reply.trim() });
      replaceRequest(data.request); setReply(""); setSection("open");
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const upload = async (file, requestId = null) => {
    if (!file) return;
    setBusy(true); setError("");
    const form = new FormData(); form.append("attachment", file);
    try {
      const { data } = await api.post(
        requestId ? `/me/requests/${requestId}/attachments` : "/me/requests/attachments", form,
      );
      replaceRequest(data.request); setSection("open"); setSearchParams({ request: data.request.id });
    } catch { setError(t("requestUploadFailed")); }
    finally { setBusy(false); }
  };
  const voiceSent = async (updated) => {
    replaceRequest(updated); setSection("open"); setSearchParams({ request: updated.id });
  };
  const closeDetail = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("request");
      return next;
    }, { replace: true });
    deepLinkRef.current = null;
  };

  const list = section === "resolved" ? resolvedRequests : activeRequests;
  return <div ref={shellRef} className={`player-requests-shell min-h-screen ${selected ? "player-requests-shell--detail" : "safe-bottom"}`} style={{ background: "var(--pc-black)" }}>
    <header className={`player-requests-global-header px-5 pt-6 pb-3 safe-top ${selected ? "hidden lg:flex" : "flex"} justify-between items-start border-b`} style={{ borderColor: "var(--pc-border)" }}>
      <div><p className="text-sm" style={{ color: "var(--pc-muted)" }}>{t("support")}</p><h1 className="text-3xl font-display font-bold">{t("requests")}</h1></div>
      <div className="flex items-center gap-2"><PlayerNotificationBell /><LanguageSwitcher compact /></div>
    </header>
    <main className={`player-requests-main max-w-6xl mx-auto ${selected ? "p-0 lg:p-5 lg:pb-36" : "p-5 pb-36"}`}>
      {!selected && <p className="text-sm mb-4" style={{ color: "var(--pc-muted)" }}>{t("requestsIntro")}</p>}
      <div className={`${selected ? "hidden lg:flex" : "grid grid-cols-3 lg:flex"} gap-2 pb-2`} data-testid="player-request-sections">
        {[['open', t('openRequests')], ['resolved', t('resolvedRequests')], ['new', t('newRequest')]].map(([key, label]) =>
          <button type="button" key={key} onClick={() => { setSection(key); if (key === "new") setSearchParams({}); }}
            className="pc-btn pc-player-section-btn" style={{ background: section === key ? "var(--pc-cyan)" : "var(--pc-charcoal)", color: section === key ? "#001014" : "var(--pc-muted)" }}>{label}</button>)}
      </div>

      {section === "new" && <section className="pc-card p-5 mt-5 max-w-2xl" data-testid="new-request-form">
        <h2 className="text-xl font-display font-semibold">{t("whatDoYouNeed")}</h2>
        <textarea className="pc-input mt-4" rows="5" value={message} onChange={(event) => setMessage(event.target.value)} placeholder={t("requestPlaceholder")} />
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" disabled={busy || !message.trim()} onClick={createText} className="pc-btn pc-btn-primary flex items-center gap-2" data-testid="create-text-request"><Send size={16} /> {t("send")}</button>
          <PlayerVoiceRequest onSent={voiceSent} triggerProps={{
            className: "pc-btn pc-btn-ghost flex items-center gap-2",
            children: <><Mic size={16} /> {t("voice")}</>,
          }} />
          <label className="pc-btn pc-btn-ghost flex items-center gap-2"><Image size={16} /> {t("photo")}<input type="file" accept="image/*" hidden onChange={(event) => upload(event.target.files?.[0])} /></label>
          <label className="pc-btn pc-btn-ghost flex items-center gap-2"><File size={16} /> {t("file")}<input type="file" accept="image/*,application/pdf,text/plain,.doc,.docx" hidden onChange={(event) => upload(event.target.files?.[0])} /></label>
        </div>
        <p className="text-xs mt-3" style={{ color: "var(--pc-muted)" }}>{t("classificationHint")}</p>
      </section>}

      {section !== "new" && <div className={`player-request-layout grid lg:grid-cols-[360px_1fr] gap-5 ${selected ? "mt-0 lg:mt-5" : "mt-5"}`}>
        <section className={`${selected ? "hidden lg:block" : "block"} space-y-2`} data-testid="player-request-list">
          {state === "loading" && <div className="flex justify-center py-16"><div className="pc-spinner" /></div>}
          {state === "ready" && list.length === 0 && <div className="pc-card p-6 text-sm" style={{ color: "var(--pc-muted)" }}>{t(section === "resolved" ? "noRequestsHere" : "requestsEmptyOpen")}</div>}
          {list.map((request) => <button type="button" key={request.id} onClick={() => openRequest(request.id)}
            className="pc-card pc-request-list-card w-full text-left" style={{ borderColor: selected?.id === request.id ? "var(--pc-cyan)" : undefined }} data-testid={`player-request-${request.id}`} data-completed={request.status === "resolved"}>
            <p className="pc-request-list-card__title line-clamp-2">{playerRequestTitle(request, t)}</p>
            <span className="pc-request-list-card__meta"><Status request={request} /><span aria-hidden="true">·</span><time dateTime={request.updated_at || undefined} title={when(request.updated_at, user?.timezone, lang)}>{playerCommunicationTime(request.updated_at, user?.timezone, lang)}</time>{request.has_audio && <Mic size={14} aria-label={t("voice")} />}{request.has_attachment && <Paperclip size={14} aria-label={t("attachment")} />}</span>
          </button>)}
        </section>
        <section className={`player-request-detail-section ${selected ? "block" : "hidden lg:block"}`}>{selected ? <div className="player-request-thread pc-card overflow-hidden" data-testid="player-request-thread">
          <header className="player-request-chat-header p-3 lg:p-5 border-b" style={{ borderColor: "var(--pc-border)" }} data-testid="player-request-chat-header">
            <div className="flex items-center gap-2.5">
              <button type="button" className="lg:hidden w-11 h-11 shrink-0 rounded-full grid place-items-center" style={{ background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)" }} onClick={closeDetail} aria-label={t("back")} data-testid="request-detail-back"><ArrowLeft size={17} /></button>
              <div className="min-w-0 flex-1">{selectedTopic.length > 48
                ? <details className="pc-request-topic"><summary><h2 className="pc-request-topic__title font-display font-semibold">{selectedTopic}</h2></summary></details>
                : <h2 className="pc-request-topic__title font-display font-semibold">{selectedTopic}</h2>}
                <p className="text-[11px] lg:text-xs mt-0.5" style={{ color: "var(--pc-muted)" }}>{t(selected.category)}</p></div>
              <Status request={selected} />
            </div>
            {selected.context?.title && <p className="hidden lg:block text-xs mt-3" style={{ color: "var(--pc-muted)" }}>{t("context")}: {selected.context.title}</p>}
            {selected.linked_service && <div className="rounded-xl p-2.5 lg:p-3 mt-2 lg:mt-4 flex items-center justify-between gap-3" style={{ background: "var(--pc-charcoal-2)", border: "1px solid var(--pc-border)" }} data-testid="player-request-linked-service">
              <div className="min-w-0"><p className="text-xs" style={{ color: "var(--pc-muted)" }}>{t("linkedService")}</p><p className="text-sm font-semibold truncate">{selected.linked_service.title}</p><p className="text-xs mt-1" style={{ color: "var(--pc-muted)" }}>{selected.linked_service.scheduled_start_at ? when(selected.linked_service.scheduled_start_at, user?.timezone, lang) : t("datePending")} · {t(`servicePlayer_${selected.linked_service.player_presentation?.state || "organizing"}`)}</p></div>
              <button type="button" className="pc-btn pc-btn-ghost shrink-0" style={{ padding: "8px 12px" }} onClick={() => nav(`/services?service=${selected.linked_service.id}`)} data-testid="player-request-to-service">{t("toService")}</button>
            </div>}
          </header>
          <div ref={messageAreaRef} onScroll={(event) => {
            const area = event.currentTarget;
            followLatestRef.current = area.scrollHeight - area.scrollTop - area.clientHeight <= 96;
          }} className="player-request-messages p-3 lg:max-h-[52vh] overflow-y-auto pc-scroll" data-testid="player-request-messages">{selected.messages.map((item, index) => <PlayerRequestMessage key={item.id} message={item} previousMessage={selected.messages[index - 1]} timezone={user?.timezone} />)}</div>
          <footer className="player-request-composer-footer lg:p-4 lg:border-t" style={{ borderColor: "var(--pc-border)" }}>
            {selected.status === "resolved" && !selected.linked_service && <p className="text-xs mb-3" style={{ color: "#7fd18a" }}>{t("replyReopens")}</p>}
            {!(selected.status === "resolved" && selected.linked_service) && <PlayerRequestComposer
              key={selected.id}
              value={reply}
              onChange={setReply}
              onSend={sendReply}
              requestId={selected.id}
              contextTitle={selectedTopic}
              onVoiceSent={voiceSent}
              onAttachment={(file) => upload(file, selected.id)}
              busy={busy}
            />}
          </footer>
        </div> : <div className="pc-card p-8 text-center" style={{ color: "var(--pc-muted)" }}>{t("selectRequest")}</div>}</section>
      </div>}
      {error && <p className="text-sm mt-4" style={{ color: "#ff8a8a" }} data-testid="requests-error">{error}</p>}
    </main>
    <PlayerNavigation active="requests" onVoiceSent={voiceSent} contextLabel={null} hideVoice={Boolean(selected)} hideNavigationOnMobile={Boolean(selected)} />
    <PlayerRequestViewportDebug enabled={Boolean(selected) && viewportDebugEnabled} shellRef={shellRef} />
  </div>;
}
